/**
 * Single source of truth for integration feature endpoints across hosts.
 *
 * HOW TO CHANGE AN ENDPOINT
 * =========================
 * Edit the corresponding field below for the host whose behavior is changing
 * (`legacy` or `ispm`). The API client and router provider read from here.
 *
 * When an endpoint should be the same across hosts, put the same value in
 * both entries. It's deliberate duplication — it keeps this file self-
 * describing and lets any endpoint be overridden later without touching
 * call-site code.
 *
 * HOST MARKERS
 * ============
 * - `ispm`       — window.__MFE_HOST__ === "ispm" (set by apps/ispm host)
 * - `legacy`     — any other window.__MFE_HOST__ value, OR pathname under
 *                  /ECMv6/* (ui-track single-spa host)
 * - `standalone` — no host marker and not on an ECMv6 path (integrations
 *                  MFE's own dev server)
 */

/** Which of the three base HTTP clients to dispatch through. */
export type ClientKind = "onboarding" | "hydra" | "idwAdmin" | "idwAdminV1";

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface ApiEndpoint {
  client: ClientKind;
  /** Relative path against the chosen client's base URL. */
  path: string;
  method: HttpMethod;
}

export type HostKind = "legacy" | "ispm" | "standalone";

export interface IntegrationEndpointConfig {
  routes: {
    /** Frontend route prefix for the integrations feature. */
    basepath: string;
  };
  api: {
    list: ApiEndpoint;
    getById: (integrationId: number) => ApiEndpoint;
    getCredentials: (integrationId: number) => ApiEndpoint;
    getTypes: ApiEndpoint;
    getTypeFeatures: (integrationType: string) => ApiEndpoint;
    getFeatureResourceTypes: (featureName: string) => ApiEndpoint;
    /** Create path depends on integration type + host. */
    create: (integrationType: string) => ApiEndpoint;
    update: (integrationId: number) => ApiEndpoint;
    delete: (integrationId: number) => ApiEndpoint;
    testConnection: ApiEndpoint;
    downloadCft: ApiEndpoint;
  };
}

const onboardingList: ApiEndpoint = {
  client: "onboarding",
  path: "/integrations",
  method: "GET",
};
const onboardingGetById = (id: number): ApiEndpoint => ({
  client: "onboarding",
  path: `/integrations/${id}`,
  method: "GET",
});
const onboardingGetCredentials = (id: number): ApiEndpoint => ({
  client: "onboarding",
  path: `/integrations/${id}/credentials`,
  method: "GET",
});
const onboardingGetTypes: ApiEndpoint = {
  client: "onboarding",
  path: "/integration-types",
  method: "GET",
};
const onboardingGetTypeFeatures = (type: string): ApiEndpoint => ({
  client: "onboarding",
  path: `/integration-types/${type}/features`,
  method: "GET",
});
const onboardingGetFeatureResourceTypes = (
  featureName: string,
): ApiEndpoint => ({
  client: "onboarding",
  path: `/features/${featureName}/resource-types`,
  method: "GET",
});
const onboardingUpdate = (id: number): ApiEndpoint => ({
  client: "onboarding",
  path: `/integrations/${id}`,
  method: "PUT",
});
const onboardingDelete = (id: number): ApiEndpoint => ({
  client: "onboarding",
  path: `/integrations/${id}`,
  method: "DELETE",
});
const hydraTestConnection: ApiEndpoint = {
  client: "hydra",
  path: "/connection/test",
  method: "POST",
};
const onboardingDownloadCft: ApiEndpoint = {
  client: "onboarding",
  path: "/integrations/aws-cft/download",
  method: "POST",
};

import { INTEGRATION_TYPES } from "./integrationTypes";

const idwAdminCreateEndpoint: ApiEndpoint = {
  client: "idwAdmin",
  path: "/integrationProvisioning/createEndpoint",
  method: "POST",
};
const idwAdminV1CreateApplications: ApiEndpoint = {
  client: "idwAdminV1",
  path: "/integration-provisioning/applications?persist_mappings=true",
  method: "POST",
};
const onboardingCreateByType = (type: string): ApiEndpoint => ({
  client: "onboarding",
  path: `/integrations/${type}`,
  method: "POST",
});

// ─── Per-host configs ─────────────────────────────────────────────────────
const legacyConfig: IntegrationEndpointConfig = {
  routes: { basepath: "/intelligence/integrations" },
  api: {
    list: onboardingList,
    getById: onboardingGetById,
    getCredentials: onboardingGetCredentials,
    getTypes: onboardingGetTypes,
    getTypeFeatures: onboardingGetTypeFeatures,
    getFeatureResourceTypes: onboardingGetFeatureResourceTypes,
    // AWS stays on onboarding; all other types go through idwAdmin.
    create: (type) => {
      if (type === INTEGRATION_TYPES.AWS) {
        return onboardingCreateByType(type);
      }
      if (type === INTEGRATION_TYPES.SAP_SUCCESS_FACTORS) {
        return idwAdminV1CreateApplications;
      }
      return idwAdminCreateEndpoint;
    },
    update: onboardingUpdate,
    delete: onboardingDelete,
    testConnection: hydraTestConnection,
    downloadCft: onboardingDownloadCft,
  },
};

const ispmConfig: IntegrationEndpointConfig = {
  routes: { basepath: "/integrations" },
  api: {
    list: onboardingList,
    getById: onboardingGetById,
    getCredentials: onboardingGetCredentials,
    getTypes: onboardingGetTypes,
    getTypeFeatures: onboardingGetTypeFeatures,
    getFeatureResourceTypes: onboardingGetFeatureResourceTypes,
    // All types use onboarding/integrations/{type}.
    create: onboardingCreateByType,
    update: onboardingUpdate,
    delete: onboardingDelete,
    testConnection: hydraTestConnection,
    downloadCft: onboardingDownloadCft,
  },
};

// Standalone (MFE's own dev server) mirrors ispm.
const standaloneConfig: IntegrationEndpointConfig = ispmConfig;

export const INTEGRATION_ENDPOINTS: Record<
  HostKind,
  IntegrationEndpointConfig
> = {
  legacy: legacyConfig,
  ispm: ispmConfig,
  standalone: standaloneConfig,
};

export function detectHost(): HostKind {
  if (typeof window === "undefined") return "standalone";

  const marker = (window as Window & { __MFE_HOST__?: string }).__MFE_HOST__;
  if (marker === "ispm") return "ispm";
  if (marker) return "legacy";

  // Fallback: ui-track single-spa host doesn't set __MFE_HOST__ but serves
  // the MFE under /ECMv6/*.
  if (
    window.location.pathname.startsWith("/ECMv6") ||
    window.location.hostname === "loopback"
  ) {
    return "legacy";
  }

  return "standalone";
}

export function getIntegrationEndpoints(): IntegrationEndpointConfig {
  return INTEGRATION_ENDPOINTS[detectHost()];
}
